import { useState } from 'react'
import { API_BASE_URL } from '@/services/api'
import type { MediaFile } from '@/types/message'

interface MediaUploadOptions {
  category: 'message' | 'table' | 'character' | 'user'
  entityId: string
  tableId?: string
  isShared?: boolean  // Se true, mídia já é compartilhada no grupo (envio rápido)
}

interface UseMediaUploadReturn {
  uploadMedia: (file: File, options: MediaUploadOptions) => Promise<MediaFile>
  isUploading: boolean
  progress: number
  error: string | null
}

export function useMediaUpload(): UseMediaUploadReturn {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const uploadMedia = async (
    file: File,
    options: MediaUploadOptions
  ): Promise<MediaFile> => {

    setIsUploading(true)
    setProgress(0)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', options.category)
      formData.append('entity_id', options.entityId)
      if (options.tableId) {
        formData.append('table_id', options.tableId)
      }
      // Envio rápido: mídia já compartilhada
      if (options.isShared) {
        formData.append('is_shared', 'true')
      }

      const token = localStorage.getItem('auth_token')
      if (!token) {
        throw new Error('Token de autenticação não encontrado')
      }

      const xhr = new XMLHttpRequest()

      return new Promise<MediaFile>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100
            setProgress(percentComplete)
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText)
              setProgress(100)
              setIsUploading(false)
              resolve(response)
            } catch (e) {
              setIsUploading(false)
              setError('Erro ao processar resposta do servidor')
              reject(new Error('Erro ao processar resposta do servidor'))
            }
          } else {
            setIsUploading(false)
            let errorMessage = 'Erro ao fazer upload'
            try {
              const errorData = JSON.parse(xhr.responseText)
              errorMessage = errorData.detail || errorMessage
            } catch (e) {
              errorMessage = `Erro HTTP ${xhr.status}`
              console.error('[UPLOAD] Erro HTTP:', xhr.status, xhr.statusText)
            }
            setError(errorMessage)
            reject(new Error(errorMessage))
          }
        })

        xhr.addEventListener('error', () => {
          setIsUploading(false)
          setError('Erro de conexão com o servidor')
          reject(new Error('Erro de conexão com o servidor'))
        })

        xhr.addEventListener('abort', () => {
          setIsUploading(false)
          setError('Upload cancelado')
          reject(new Error('Upload cancelado'))
        })

        xhr.open('POST', `${API_BASE_URL}/media/upload`)
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        xhr.send(formData)
      })
    } catch (err) {
      setIsUploading(false)
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      setError(errorMessage)
      throw err
    }
  }

  return {
    uploadMedia,
    isUploading,
    progress,
    error
  }
}
